import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import classNames from 'classnames';

import PhotoSelect from './components/PhotoSelect';
import PhotoSelectionOverlayNav from './components/PhotoSelectionOverlayNav';
import Button from '../../../../../../../../components/global/Button';
import DismissButton from '../../../../../../../../components/global/DismissButton';
import AlbumsIcon from '../../../../../../../../components/icons/AlbumsIcon';
import useVirtualPageView from '../../../../../../services/analytics/useVirtualPageView';
import { createPhotoSelectionCandidatePhotoGroups } from './helpers';
import getSlideshowDesignerConfigurationPhotos from '../../../queries/getAlbumSlideshowDesignerConfigurationPhotos';
import { ALBUM_SLIDESHOW_MODE } from '../../../../../../../../constants/albums';
import CardElementPhotoSelectionControlGridSelect, {
  GRID_TYPES,
} from '../../../../../../../../services/cards/components/CardElementPhoto/CardElementPhotoSelectionControl/CardElementPhotoSelectionControlGridSelect';

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  defaultPhotoGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      total: PropTypes.number,
      photos: PropTypes.array,
      loadMore: PropTypes.func,
    }),
  ),
  onClose: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  currentSelectionMode: PropTypes.oneOf(Object.values(ALBUM_SLIDESHOW_MODE)),
  currentExcludedPhotos: PropTypes.arrayOf(
    PropTypes.shape({
      photo: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  ),
  currentSelectedPhotos: PropTypes.arrayOf(
    PropTypes.shape({
      photo: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  ),
};

const PhotoSelectionOverlay = ({
  album,
  defaultPhotoGroups = [],
  onClose,
  onChange,
  currentSelectionMode,
  currentExcludedPhotos,
  currentSelectedPhotos,
}) => {
  const photoGroups = [...defaultPhotoGroups];
  const [saving, setSaving] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectionMode, setSelectionMode] = useState(
    currentSelectionMode ?? ALBUM_SLIDESHOW_MODE.ALL_PHOTOS,
  );
  const [excludedPhotos, setExcludedPhotos] = useState(
    (currentExcludedPhotos ?? []).map(({ photo }) => photo),
  );
  const [selectedPhotos, setSelectedPhotos] = useState(
    (currentSelectedPhotos ?? []).map(({ photo }) => photo),
  );
  const [activePhotoGroupIndex, setActivePhotoGroupIndex] = useState(0);
  const [gridType, setGridType] = useState(GRID_TYPES.SINGLE);
  const activePhotoGroup = photoGroups[activePhotoGroupIndex] || {};
  const { photos = [], loadMore, total = 0 } = activePhotoGroup;

  // eslint-disable-next-line no-undef
  const { onVirtualPageViewTrack } = useVirtualPageView({ fireOnMount: false });

  useEffect(() => {
    if (activePhotoGroup) {
      onVirtualPageViewTrack({
        pageViewTitle: `Slideshow Designer - Select a photo from: ${activePhotoGroup.label}`,
        pageViewPath: `/photo/select/${activePhotoGroup.label}`,
      });
    }
  }, [activePhotoGroup]);

  useEffect(() => {
    if (selectedPhotos.length === total && total > 0) {
      setSelectedPhotos([]);
      setExcludedPhotos([]);
      setSelectionMode(ALBUM_SLIDESHOW_MODE.ALL_PHOTOS);
    }
  }, [selectedPhotos]);

  const onDone = async () => {
    setSaving(true);
    await onChange({
      selectionMode,
      excludedPhotos,
      selectedPhotos,
    });
    setSaving(false);
    onClose();
  };

  const photoListClasses = classNames({
    'card-photo-selection-control-photo-list': true,
    'single-column': gridType === GRID_TYPES.SINGLE,
    'double-columns': gridType === GRID_TYPES.DOUBLE,
  });

  return (
    <div className="card-element-photo-selection-control-content">
      <div className="card-element-control-header">
        <span className="card-element-control-header-title">Pick a photo</span>
        <DismissButton onClick={onClose} disabled={saving} />
      </div>
      {album && (
        <h2 className="d-flex align-items-center w-100">
          <AlbumsIcon size={24} color="#000" className="mr-3" />
          {album.name}
        </h2>
      )}
      <PhotoSelectionOverlayNav
        photoGroups={photoGroups}
        activePhotoGroupIndex={activePhotoGroupIndex}
        onPhotoGroupIndexChange={setActivePhotoGroupIndex}
        onChangeSelectionMode={isSelectAll => {
          // Photo Group 1 = Matches, which won't be there all the time
          if (activePhotoGroupIndex === 1) {
            //  Since we're looking at matches, we don't really change
            //  selection mode. Instead, we focus on adding / removing
            //  the photos from matches based on the clicked button.
            if (selectionMode === ALBUM_SLIDESHOW_MODE.SELECTED_PHOTOS) {
              setSelectedPhotos(
                isSelectAll
                  ? [
                      ...selectedPhotos,
                      ...photos.filter(
                        p => !selectedPhotos.find(sp => sp.id === p.id),
                      ),
                    ]
                  : [
                      ...selectedPhotos.filter(
                        sp => !photos.find(p => p.id === sp.id),
                      ),
                    ],
              );
            } else {
              setExcludedPhotos(
                isSelectAll
                  ? [
                      ...excludedPhotos.filter(
                        sp => !photos.find(p => p.id === sp.id),
                      ),
                    ]
                  : [
                      ...excludedPhotos,
                      ...photos.filter(
                        p => !excludedPhotos.find(sp => sp.id === p.id),
                      ),
                    ],
              );
            }
          } else {
            setSelectionMode(
              isSelectAll
                ? ALBUM_SLIDESHOW_MODE.ALL_PHOTOS
                : ALBUM_SLIDESHOW_MODE.SELECTED_PHOTOS,
            );
            setSelectedPhotos([]);
            setExcludedPhotos([]);
          }
        }}
      />
      {activePhotoGroup?.id !== 'custom-upload' && (
        <Fragment>
          <CardElementPhotoSelectionControlGridSelect
            onGridTypeChange={newGridType => setGridType(newGridType)}
          />
          <div className="card-photo-selection-control-scroll">
            <div className={photoListClasses} key={activePhotoGroup.id}>
              {photos.map(record => {
                const photo = record.photo || record;

                return (
                  <PhotoSelect
                    key={record?.id ?? photo.id}
                    photo={photo}
                    onSelect={() => {
                      if (
                        selectionMode === ALBUM_SLIDESHOW_MODE.SELECTED_PHOTOS
                      ) {
                        setSelectedPhotos(
                          selectedPhotos.find(p => p.id === photo.id)
                            ? selectedPhotos.filter(p => p.id !== photo.id)
                            : [...selectedPhotos, photo],
                        );
                      } else {
                        setExcludedPhotos(
                          excludedPhotos.find(p => p.id === photo.id)
                            ? excludedPhotos.filter(p => p.id !== photo.id)
                            : [...excludedPhotos, photo],
                        );
                      }
                    }}
                    selected={
                      selectionMode === ALBUM_SLIDESHOW_MODE.SELECTED_PHOTOS
                        ? selectedPhotos.find(p => p.id === photo.id)
                        : !excludedPhotos.find(p => p.id === photo.id)
                    }
                    isSingleGrid={gridType === GRID_TYPES.SINGLE}
                  />
                );
              })}
            </div>
          </div>
        </Fragment>
      )}
      {loadMore && photos.length < total && (
        <div className="card-photo-selection-control-photo-list-actions">
          <Button
            btnStyle="link"
            className="mb-2"
            onClick={async () => {
              setLoadingMore(true);
              await loadMore();
              setLoadingMore(false);
            }}
            btnBlock
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : 'Load more'}
          </Button>
        </div>
      )}

      <div className="card-element-control-footer">
        <Button
          btnBlock
          onClick={onDone}
          disabled={
            saving ||
            (selectionMode === ALBUM_SLIDESHOW_MODE.SELECTED_PHOTOS &&
              selectedPhotos.length === 0)
          }
        >
          {saving ? 'Saving...' : 'Done'}
        </Button>
      </div>
    </div>
  );
};

PhotoSelectionOverlay.propTypes = propTypes;

const withCandidatePhotos = graphql(getSlideshowDesignerConfigurationPhotos, {
  options: ({ albumId }) => ({
    variables: {
      album_id: albumId,
    },
  }),
  props: ({ data: { me, fetchMore, loading } = {} }) => {
    return {
      loading,
      album: me?.albums?.records?.[0],
      defaultPhotoGroups: createPhotoSelectionCandidatePhotoGroups({
        source: me,
        mappings: [
          {
            id: 'allPhotos',
            label: 'All',
            path: 'albums.records[0].photos',
          },
          {
            id: 'matches',
            label: 'Matches',
            path: 'matched_photos',
          },
        ],
        fetchMore,
      }),
    };
  },
  skip: ({ albumId }) => !albumId,
});

export default compose(withCandidatePhotos)(PhotoSelectionOverlay);
